/* eslint-disable */
import React from 'react';
import {
  Box, Typography, Checkbox, FormGroup, Divider, Skeleton,
} from '@mui/material';
import { PRODUCT_FILTERS } from './constants';

/* ── Filter section wrapper ──────────────────────────────────── */
const FilterSection = ({ title, children }) => (
  <Box sx={{ mb: '4px', width: '100%' }}>
    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#2D3748', mb: '2px' }}>
      {title}
    </Typography>
    {children}
  </Box>
);

/* ── Filter checkbox item ────────────────────────────────────── */
const FilterCheckbox = ({ label, count, countLoading, checked, onChange }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      mb: '2px',
      width: '100%',
      overflow: 'hidden',
    }}
  >
    <Checkbox
      checked={checked}
      onChange={onChange}
      size="small"
      sx={{
        p: '2px',
        color: '#717182',
        flexShrink: 0,
        '&.Mui-checked': { color: '#007BC6' },
        '& .MuiSvgIcon-root': { fontSize: 18 },
      }}
    />
    <Typography
      title={label}
      sx={{
        fontSize: '11px', color: '#717182', fontWeight: 600,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        flex: 1, minWidth: 0, cursor: 'pointer', ml: '2px',
      }}
      onClick={onChange}
    >
      {label}
    </Typography>
    <Box sx={{ flexShrink: 0, ml: '4px', display: 'flex', alignItems: 'center' }}>
      {countLoading
        ? <Skeleton variant="text" width={28} height={14} />
        : count !== undefined && (
          <Typography sx={{ fontSize: '11px', color: '#8397AE', fontWeight: 600, whiteSpace: 'nowrap' }}>
            ({count})
          </Typography>
        )
      }
    </Box>
  </Box>
);

/* ── FilterSidebar ───────────────────────────────────────────── */
const FilterSidebar = ({
  search,
  familyFilters, toggleFamilyFilter,
  brandFilters, toggleBrandFilter,
  productFilters, toggleProductFilter,
  allFamilies,
  allBrands,
  priceMin, setPriceMin, priceMax, setPriceMax,
  onApplyPrice, resetAllFilters,
  filterCounts, filterCountsLoading,
}) => {
  /* Build count lookup maps */
  const familyCountMap = {};
  (filterCounts?.families || []).forEach((f) => { if (f.id != null) familyCountMap[f.id] = f.count; });
  const brandCountMap = {};
  (filterCounts?.brands || []).forEach((b) => { brandCountMap[b.name] = b.count; });
  const productCountMap = {
    new: filterCounts?.isNew,
    available: filterCounts?.isAvailable,
    promo: filterCounts?.isPromo,
  };

  return (
  <Box
    sx={{
      backgroundColor: '#FFFFFF',
      border: '1px solid #D1D5DC',
      borderRadius: '12px',
      boxShadow: '0px 6px 26px #626B7D12',
      p: '16px',
      position: 'sticky',
      top: '86px',
      width: '100%',
      boxSizing: 'border-box',
      maxHeight: 'calc(100vh - 106px)',
      overflowY: 'hidden',
    }}
  >
    {/* Sidebar header */}
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: '4px',
      }}
    >
      <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#000000', fontFamily: "'Open Sans', sans-serif", textTransform: 'capitalize', lineHeight: '32px' }}>
        Filtres
      </Typography>
      <Typography
        onClick={resetAllFilters}
        sx={{
          fontSize: '14px',
          fontWeight: 600,
          color: '#007BC6',
          cursor: 'pointer',
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        Réinitialiser
      </Typography>
    </Box>

    <Divider sx={{ borderColor: '#F0F4F8', mb: '4px' }} />

    {/* Categories — in search mode shows only families present in results */}
    {allFamilies.length > 0 && (
      <>
        <FilterSection title="Familles">
          <Box sx={{ maxHeight: 'clamp(60px, calc((100vh - 570px) / 2), 220px)', overflowY: 'auto', pr: '2px', '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-thumb': { backgroundColor: '#DEE5EE', borderRadius: '4px' } }}>
            <FormGroup>
              {allFamilies.map((cat) => (
                <FilterCheckbox
                  key={cat.id}
                  label={cat.name}
                  count={familyCountMap[cat.id]}
                  countLoading={filterCountsLoading}
                  checked={familyFilters.includes(cat.id)}
                  onChange={() => toggleFamilyFilter(cat.id)}
                />
              ))}
            </FormGroup>
          </Box>
        </FilterSection>

        <Divider sx={{ borderColor: '#F0F4F8', mb: '4px' }} />
      </>
    )}

    {/* Brands */}
    {allBrands.length > 0 && (
      <FilterSection title="Marque">
        <Box sx={{ maxHeight: 'clamp(60px, calc((100vh - 570px) / 2), 220px)', overflowY: 'auto', pr: '2px', '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-thumb': { backgroundColor: '#DEE5EE', borderRadius: '4px' } }}>
          <FormGroup>
            {allBrands.map((brand) => (
              <FilterCheckbox
                key={brand}
                label={brand}
                count={search ? (brandCountMap[brand] ?? 0) : brandCountMap[brand]}
                countLoading={filterCountsLoading}
                checked={brandFilters.includes(brand)}
                onChange={() => toggleBrandFilter(brand)}
              />
            ))}
          </FormGroup>
        </Box>
      </FilterSection>
    )}

    <Divider sx={{ borderColor: '#F0F4F8', mb: '4px' }} />

    {/* Product type */}
    <FilterSection title="Produit">
      <FormGroup>
        {PRODUCT_FILTERS.map((pf) => (
          <FilterCheckbox
            key={pf.id}
            label={pf.name}
            count={productCountMap[pf.id]}
            countLoading={filterCountsLoading}
            checked={productFilters.includes(pf.id)}
            onChange={() => toggleProductFilter(pf.id)}
          />
        ))}
      </FormGroup>
    </FilterSection>

    <Divider sx={{ borderColor: '#F0F4F8', mb: '4px' }} />

    {/* Prix */}
    <FilterSection title="Prix">
      <Box sx={{ display: 'flex', gap: '10px', mb: '12px', width: '100%' }}>
        <input
          type="number"
          placeholder="Min"
          value={priceMin}
          onChange={(e) => setPriceMin(e.target.value)}
          style={{
            width: '50%',
            minWidth: 0,
            boxSizing: 'border-box',
            border: '1px solid #DEE5EE',
            borderRadius: '8px',
            padding: '10px 12px',
            fontSize: '14px',
            color: '#4A5568',
            outline: 'none',
            backgroundColor: '#F8FAFC',
            fontFamily: "'Open Sans', sans-serif",
          }}
        />
        <input
          type="number"
          placeholder="Max"
          value={priceMax}
          onChange={(e) => setPriceMax(e.target.value)}
          style={{
            width: '50%',
            minWidth: 0,
            boxSizing: 'border-box',
            border: '1px solid #DEE5EE',
            borderRadius: '8px',
            padding: '10px 12px',
            fontSize: '14px',
            color: '#4A5568',
            outline: 'none',
            backgroundColor: '#F8FAFC',
            fontFamily: "'Open Sans', sans-serif",
          }}
        />
      </Box>
      <Box
        onClick={onApplyPrice}
        sx={{
          width: '100%',
          boxSizing: 'border-box',
          border: '1px solid #DEE5EE',
          borderRadius: '8px',
          py: '10px',
          textAlign: 'center',
          fontSize: '14px',
          fontWeight: 600,
          color: '#4A5568',
          cursor: 'pointer',
          backgroundColor: '#F8FAFC',
          fontFamily: "'Open Sans', sans-serif",
          '&:hover': { backgroundColor: '#EEF2F7' },
        }}
      >
        Appliquer
      </Box>
    </FilterSection>
  </Box>
  );
};

export default FilterSidebar;
